import { useState, useRef } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

interface UseImageUploadOptions {
  storageKey?: string;
  errorMessage?: string;
}

export const useImageUpload = (options?: UseImageUploadOptions) => {
  const { t } = useTranslation();
  const storageKey = options?.storageKey || 'onboarding_avatar_preview';
  const errorMessage = options?.errorMessage || t('auth.onboarding.profile.avatarSizeError');
  
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > MAX_FILE_SIZE) {
        toast.error(errorMessage);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        return;
      }

      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setPreview(previewUrl);
        
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(storageKey, previewUrl);
        }
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleRemove = () => {
    setFile(null);
    setPreview(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(storageKey);
    }
  };

  return {
    file,
    preview,
    inputRef,
    handleChange,
    handleRemove,
  };
};
